import type { components } from '../../api/generated/schema'
import { formatCents, parseMoney } from '../../api/parseMoney'
import type { ENUM_VALUES } from '../../platform/analytics/vocabulary'
import type { LocalDate } from '../../platform/localDate'

/**
 * The only place a budget response is read, and the only place a budget request
 * body is written. Everything past this module sees an `Envelope`: money in
 * integer cents, the percentage as a number, the flags exactly as the server
 * sent them. `remaining` is the server's figure (clamped at 0) and is never
 * derived; `is_alert` / `is_over_budget` are never recomputed.
 *
 * Any shape problem is ONE `BudgetContractError` naming the field.
 */

export const BUDGET_CURRENCY = 'CAD'
export const DEFAULT_ALERT_PCT = 80

export type BudgetWire = components['schemas']['BudgetResponse']
export type BudgetPeriod = 'weekly' | 'monthly' | 'yearly'
export type BudgetField = (typeof ENUM_VALUES.budget_field)[number]

export type EnvelopeProgress = {
  /** progress.spent, integer cents, >= 0 */
  spentCents: number
  /** progress.remaining, integer cents, CLAMPED at 0 by the server — never derive it */
  remainingCents: number
  /** progress.percentage as a number, one decimal; may exceed 100 */
  percentage: number
  isOverBudget: boolean
  /** true when over, too */
  isAlert: boolean
}

export type Envelope = {
  id: string
  name: string
  /** null = counts ALL spending (C1) */
  categoryId: string | null
  period: BudgetPeriod
  amountCents: number
  /** integer 0–100; a null alert_threshold reads as 80 (the server's own default) */
  alertThresholdPct: number
  /** false = paused (A4) */
  isActive: boolean
  progress: EnvelopeProgress
}

export type EnvelopeDraft = {
  name: string
  categoryId: string | null
  amountCents: number
  alertThresholdPct: number
}

export class BudgetContractError extends Error {
  readonly path: string
  constructor(path: string, message: string) {
    super(`Budget response: ${path}: ${message}`)
    this.name = 'BudgetContractError'
    this.path = path
  }
}

type Rec = Record<string, unknown>

function isRec(v: unknown): v is Rec {
  return typeof v === 'object' && v !== null && !Array.isArray(v)
}

function readRec(v: unknown, path: string): Rec {
  if (!isRec(v)) throw new BudgetContractError(path, 'expected an object')
  return v
}

function readString(v: unknown, path: string): string {
  if (typeof v !== 'string') throw new BudgetContractError(path, 'expected a string')
  return v
}

function readBool(v: unknown, path: string): boolean {
  if (typeof v !== 'boolean') throw new BudgetContractError(path, 'expected a boolean')
  return v
}

function readMoney(v: unknown, path: string): number {
  const s = readString(v, path)
  try {
    return parseMoney(s)
  } catch {
    throw new BudgetContractError(path, 'not a money string')
  }
}

function readPercentage(v: unknown, path: string): number {
  const s = readString(v, path)
  if (!/^\d+(\.\d+)?$/.test(s)) throw new BudgetContractError(path, 'not a percentage')
  return Number(s)
}

function readPeriod(v: unknown, path: string): BudgetPeriod {
  if (v === 'weekly' || v === 'monthly' || v === 'yearly') return v
  throw new BudgetContractError(path, 'unknown period')
}

const THRESHOLD = /^(0(\.\d{1,2})?|1(\.0{1,2})?)$/

/**
 * `alert_threshold` is a two-decimal fixed-point fraction ("0.80"). Read through
 * `parseMoney` it is integer HUNDREDTHS — which IS the percent (0.80 → 80). It
 * is not money; the parser is reused for its exact decimal handling.
 */
function readThreshold(v: unknown, path: string): number {
  if (v === null || v === undefined) return DEFAULT_ALERT_PCT
  const s = readString(v, path)
  if (!THRESHOLD.test(s)) throw new BudgetContractError(path, 'not a threshold between 0 and 1')
  return parseMoney(s)
}

export function toEnvelope(body: unknown, path = 'budget'): Envelope {
  const r = readRec(body, path)
  const p = readRec(r['progress'], `${path}.progress`)
  const categoryId = r['category_id']
  if (categoryId !== null && categoryId !== undefined && typeof categoryId !== 'string') {
    throw new BudgetContractError(`${path}.category_id`, 'expected a string or null')
  }
  return {
    id: readString(r['id'], `${path}.id`),
    name: readString(r['name'], `${path}.name`),
    categoryId: categoryId ?? null,
    period: readPeriod(r['period'], `${path}.period`),
    amountCents: readMoney(r['amount'], `${path}.amount`),
    alertThresholdPct: readThreshold(r['alert_threshold'], `${path}.alert_threshold`),
    isActive: readBool(r['is_active'], `${path}.is_active`),
    progress: {
      spentCents: readMoney(p['spent'], `${path}.progress.spent`),
      remainingCents: readMoney(p['remaining'], `${path}.progress.remaining`),
      percentage: readPercentage(p['percentage'], `${path}.progress.percentage`),
      isOverBudget: readBool(p['is_over_budget'], `${path}.progress.is_over_budget`),
      isAlert: readBool(p['is_alert'], `${path}.progress.is_alert`),
    },
  }
}

export function toEnvelopes(body: unknown): Envelope[] {
  const r = readRec(body, 'body')
  const list = r['budgets']
  if (!Array.isArray(list)) throw new BudgetContractError('budgets', 'expected a list')
  return list.map((row, i) => toEnvelope(row, `budgets[${i}]`))
}

export function toCreateBody(d: EnvelopeDraft, today: LocalDate): components['schemas']['BudgetCreate'] {
  return {
    name: d.name.trim(),
    amount: formatCents(d.amountCents),
    period: 'monthly',
    category_id: d.categoryId,
    start_date: `${today.slice(0, 8)}01`,
    alert_threshold: formatCents(d.alertThresholdPct),
  }
}

export function changedFields(before: Envelope, d: EnvelopeDraft): BudgetField[] {
  const out: BudgetField[] = []
  if (d.name.trim() !== before.name) out.push('name')
  if (d.categoryId !== before.categoryId) out.push('category')
  if (d.amountCents !== before.amountCents) out.push('amount')
  if (d.alertThresholdPct !== before.alertThresholdPct) out.push('alert_threshold')
  return out
}

export function toPatchBody(before: Envelope, d: EnvelopeDraft): components['schemas']['BudgetUpdate'] {
  const body: components['schemas']['BudgetUpdate'] = {}
  for (const f of changedFields(before, d)) {
    if (f === 'name') body.name = d.name.trim()
    else if (f === 'category') body.category_id = d.categoryId
    else if (f === 'amount') body.amount = formatCents(d.amountCents)
    else body.alert_threshold = formatCents(d.alertThresholdPct)
  }
  return body
}
